import useLabels from '../../../../hooks/useLabels';
import useStateRef from '../../../../hooks/useStateRef';
import { getClassNames } from '../../../../helpers/classHelpers';
import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../../ui/icon/Icon';
import { Icons } from '../../../../ui/icon/icons/material';
import { IconTheme } from '../../../../ui/icon/Icon.types';
import { TestIds } from '../../../../mocks/ids';
import { ChatViewLayoutProps } from '../../../../layouts/chat-view-layout/ChatViewLayout';
import { useGetMediaSizes } from '../../../../hooks/useMediaSize';
import { SvgIcons } from '../../../../ui/icon/icons/svg';
import { useLayout } from '../../../../contexts/layout/LayoutContext';
import styles from './PromptList.module.scss';
import ListItems from '../../../../ui/list-items/ListItems';
import StatusHeader from '../../../../ui/status-header/StatusHeader';
import Description from '../../../../ui/description/Description';
import FileListActions from '../../../../ui/file-list-actions/FileListActions';
import { usePromptListActions, usePromptListState } from '../../hooks/usePromptList';
import ChatWelcome from '../../../../elements/chat-welcome/ChatWelcome';
import Column from '../../../dashboards/admin/elements/grid/column/Column';
import TwoColumnGrid from '../../../dashboards/admin/elements/grid/two-column-grid/TwoColumnGrid';
import { usePrompt } from '../../contexts/PromptContext';
import { Prompt, PromptRequest } from '../../models/PromptTypes';
import { FileListActionsType } from '../../../../hooks/useFileUpload';
import FileListActionsModal from '../../../../ui/file-list-actions-modal/FileListActionsModal';
import { useAppInfo } from '../../../../contexts/app-info/AppInfoContext';
import PromptModal from '../../modals/prompt-modal/PromptModal';
import WindowHeader from '../../../../elements/window-header/WindowHeader';
import { usePromptModalMode } from '../../hooks/usePromptModalMode';
import { sortItemsBy } from '../../../../helpers/listsHelpers';

const MAX_DESCRIPTION_LENGTH = 300;

const PromptList: FC<ChatViewLayoutProps> = ({ onOpenSidebar }) => {
    const l = useLabels();
    const { isLarge, isMedium } = useGetMediaSizes();
    const { collapseLeftSidebar, expandLeftSidebar, isLeftSidebarVisible, isRightSidebarVisible } = useLayout();

    const [headerTitleVisible, setHeaderTitleVisible, headlineTitleVisibleRef] = useStateRef<boolean>();
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const offsetTopRef = useRef<number>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { userId } = useAppInfo();
    const [modalMode, setModalMode] = usePromptModalMode('edit');
    const [initialPrompt, setInitialPrompt] = useState<Prompt | undefined>(undefined);

    const { prompts, deletePrompt } = usePrompt();
    const [activeModal, setActiveModal] = useState<keyof Pick<FileListActionsType, 'copy' | 'remove'>>();

    const handleClose = useCallback(() => {
        setIsModalOpen(false);
    }, []);


    const AddPromptClicked = useCallback(() => {
        setInitialPrompt(undefined);
        setIsModalOpen(true);
    }, []);

    const handleScroll = () => {
        if (!searchContainerRef.current) return;
        const offsetTop = searchContainerRef.current.offsetTop;

        if (offsetTop === offsetTopRef.current && !headlineTitleVisibleRef.current) {
            setHeaderTitleVisible(false);
            headlineTitleVisibleRef.current = true;
        } else if (offsetTop !== offsetTopRef.current && headlineTitleVisibleRef.current) {
            setHeaderTitleVisible(true);
            headlineTitleVisibleRef.current = false;
        }
    };

    const getSelectedPromptKeys = (): { assistantKey: string; promptKey: string; prompt: PromptRequest; }[] => {
        createdFileUploadProps.onDeselectAll();
        setActiveModal(undefined);

        return createdSelectedIds
            .map(selectedId => {
                const selectedPrompt = prompts.find(p => p.key === selectedId);
                if (!selectedPrompt) return undefined;
                return {
                    assistantKey: selectedPrompt.assistant_key,
                    promptKey: selectedPrompt.key,
                    prompt: selectedPrompt as PromptRequest,
                };
            })
            .filter(Boolean) as { assistantKey: string; promptKey: string; prompt: PromptRequest; }[];
    };

    const handleDelete = async (): Promise<void> => {
        const selectedList = getSelectedPromptKeys();
        if (!selectedList.length) return;
        for (const selected of selectedList) {
            await deletePrompt(selected.assistantKey, selected.promptKey);
        }
    };

    const labels = useMemo(() => ({
        headline: l.promptManagementMenuTitle,
        description: l.promptManagementDescription,
        searchPlaceholder: l.promptGallerySearchPlaceholder,
        noResultsFound: l.promptGalleryNoResultsFound,
        promptGalleryError: l.promptGalleryError,
        createdByMe: l.promptListByMeTitle,
        sharedWithMe: l.promptListSharedTitle,
        createdBy: l.promptListCreatedBy,
        addPrompt: l.promptModalAddPrompt,
        copyPromptModalTitle: l.copyPromptModalTitle,
        copyPromptDescription: l.copyPromptDescription,
        copyPromptCta: l.copyPromptCta,
        copyPromptCancelCta: l.copyPromptCancelCta,
        deletePromptModalTitle: l.deletePromptModalTitle,
        deletePromptConfirmationText: l.deletePromptConfirmationText,
        deletePromptDeleteButton: l.deletePromptDeleteButton,
        deletePromptCancelButton: l.deletePromptCancelButton,
    }), [l]);

    const showIcon = useMemo(() => {
        if (!isLarge && onOpenSidebar) return false;
        if (isLeftSidebarVisible && (isLarge || isMedium)) return true;
        if (!isRightSidebarVisible && (isLarge || isMedium)) return true;
        if (isRightSidebarVisible && !isLeftSidebarVisible) return true;
        return false;
    }, [isLarge, onOpenSidebar, isLeftSidebarVisible, isMedium, isRightSidebarVisible]);

    const left = <>
        {showIcon && <Icon.Svg
            theme={IconTheme.dark}
            title={isLeftSidebarVisible ? '[Collapse sidebar]' : '[Expand sidebar]'}
            iconName={isLeftSidebarVisible ? SvgIcons.LeftPanelClose : SvgIcons.LeftPanelOpen}
            onClick={isLeftSidebarVisible ? () => collapseLeftSidebar() : expandLeftSidebar}
        />}
        {!showIcon && !!onOpenSidebar && <Icon.Base title={l.menuIconTitle} iconName={Icons.menu} onClick={onOpenSidebar} />}
    </>;

    useLayoutEffect(() => {
        if (searchContainerRef.current) {
            offsetTopRef.current = searchContainerRef.current.offsetTop;
        }
    }, []);

    const createdByMePrompts = useMemo(
        () => prompts.filter(item => item.created_by === userId),
        [prompts, userId]
    );

    const sharedWithMePrompts = useMemo(
        () => prompts.filter(item => item.created_by !== userId && item.is_public),
        [prompts, userId]
    );

    // Left (Created By Me)
    const {
        prompts: createdPrompts,
        selectedIds: createdSelectedIds,
        loadingIds: createdLoadingIds,
        onToggleSelectId: onToggleCreatedSelectId,
        setSelectedIds: setCreatedSelectedIds,
    } = usePromptListState({ prompts: createdByMePrompts });

    const isAllCreatedSelected = createdPrompts.length > 0 && createdSelectedIds.length === createdPrompts.length;

    const createdPromptItems = useMemo(() => {
        const sortedCreatedPrompts = sortItemsBy(createdPrompts, 'updated');
        return sortedCreatedPrompts.map((prompt) => ({
            id: prompt.key,
            name: prompt.title,
            header: <StatusHeader headline={prompt.title} />,
            footer: <Description threshold={MAX_DESCRIPTION_LENGTH} description={prompt.content} />,
            onItemSelect: () => onToggleCreatedSelectId(prompt.key),
        }));
    }, [createdPrompts, onToggleCreatedSelectId]);

    const createdActions = usePromptListActions({
        add: {
            onClick: () => {
                setModalMode('add');
                AddPromptClicked();
            },
        },
        edit: {
            onClick: () => {
                const selectedId = createdSelectedIds[0];
                const selectedPrompt = prompts.find(p => p.key === selectedId) ?? null;
                if (!selectedPrompt) return;

                setInitialPrompt(selectedPrompt);
                setModalMode('edit');
                setIsModalOpen(true);
            },
            isDisabled: createdSelectedIds.length !== 1,

        },
        copy: {
            onClick: () => {
                const selectedId = createdSelectedIds[0];
                const selectedPrompt = prompts.find(p => p.key === selectedId) ?? null;
                if (!selectedPrompt) return;

                setInitialPrompt(selectedPrompt);
                setModalMode('copy');
                setIsModalOpen(true);
            },
            isDisabled: createdSelectedIds.length !== 1,
        },
        remove: {
            onClick: () => setActiveModal('remove'),
            isDisabled: createdSelectedIds.length === 0,
        },
    });

    const createdFileUploadProps = {
        itemsSelected: createdSelectedIds,
        isAllSelected: isAllCreatedSelected,
        onSelectAll: () => setCreatedSelectedIds(createdPrompts.map(p => p.key)),
        onDeselectAll: () => setCreatedSelectedIds([]),
    };

    // Right (Shared With Me)
    const {
        prompts: sharedPrompts,
        selectedIds: sharedSelectedIds,
        loadingIds: sharedLoadingIds,
        onToggleSelectId: onToggleSharedSelectId,
        setSelectedIds: setSharedSelectedIds,
    } = usePromptListState({ prompts: sharedWithMePrompts });

    const isAllSharedSelected = sharedPrompts.length > 0 && sharedSelectedIds.length === sharedPrompts.length;

    const sharedPromptItems = useMemo(() => {
        const sortedPrompts = sortItemsBy(sharedPrompts, 'updated');
        return sortedPrompts.map((prompt) => ({
            id: prompt.key,
            name: prompt.title,
            header: <StatusHeader headline={prompt.title} />,
            footer: <>
                <Description description={prompt.content} />
                {prompt.created_by_name != null && prompt.created_by_name !== '' && (
                    <div className={styles['created-by-hint']}>{labels.createdBy}: {prompt.created_by_name}</div>
                )}
            </>,
            onItemSelect: () => onToggleSharedSelectId(prompt.key),
        }));
    }, [sharedPrompts, onToggleSharedSelectId, labels.createdBy]);

    const sharedActions = usePromptListActions({
        copy: {
            onClick: () => {
                const selectedId = sharedSelectedIds[0];
                const selectedPrompt = prompts.find(p => p.key === selectedId) ?? null;
                if (!selectedPrompt) return;

                setInitialPrompt(selectedPrompt);
                setModalMode('copy');
                setIsModalOpen(true);
            },
            isDisabled: sharedSelectedIds.length !== 1,
        },
    });

    const sharedFileUploadProps = {
        itemsSelected: sharedSelectedIds,
        isAllSelected: isAllSharedSelected,
        onSelectAll: () => setSharedSelectedIds(sharedPrompts.map(p => p.key)),
        onDeselectAll: () => setSharedSelectedIds([]),
    };

    return (
        <>
            <PromptModal isOpen={isModalOpen} onClose={handleClose} mode={modalMode} onAfterSubmit={createdFileUploadProps.onDeselectAll} initialValue={initialPrompt} />
            <FileListActionsModal
                isOpen={activeModal === 'remove'}
                modal={{
                    headline: labels.deletePromptModalTitle
                }}
                close={{
                    label: labels.deletePromptCancelButton,
                    onClick: () => setActiveModal(undefined)
                }}
                submit={{
                    label: labels.deletePromptDeleteButton,
                    onClick: () => handleDelete()
                }}
                errorId={''}
            >
                <div>
                    {labels.deletePromptConfirmationText}
                    <ul>
                        {createdSelectedIds
                            .map(id => prompts.find(p => p.key === id)?.title)
                            .filter(Boolean)
                            .map(name => (
                                <li key={name}>{name}</li>
                            ))}
                    </ul>
                </div>
            </FileListActionsModal>

            <WindowHeader
                className={getClassNames([
                    styles['header-title'],
                    !headerTitleVisible && styles.visible,
                ])}
                left={left}
            />
            <div className={styles.catalog} data-testid={TestIds.catalogView} onScroll={handleScroll}>
                <div className={styles.welcome}>
                    <ChatWelcome
                        avatar={Icons.promptGallery}
                        persona={{
                            title: labels.headline,
                            description: labels.description,
                            key: '',
                            route: '',
                            type: 'chat',
                            versions: [],
                        }}
                    />
                </div>
                <TwoColumnGrid
                    left={<Column
                        headline={<h5>{labels.createdByMe}</h5>} content={<div>
                            <FileListActions {...createdFileUploadProps} actions={createdActions} />
                            <div className={styles['file-list']}>
                                {createdPromptItems.length > 0 ? (
                                    <ListItems
                                        items={createdPromptItems}
                                        itemsSelected={createdSelectedIds}
                                        loadingItemIds={createdLoadingIds}
                                        childrenClassName={styles.children}
                                        headerClassName={styles.header}
                                    />
                                ) : (
                                    <p>{labels.noResultsFound}</p>
                                )}
                            </div>
                        </div>
                        }
                    />}

                    right={<Column
                        headline={<h5>{labels.sharedWithMe}</h5>} content={
                            <div>
                                <FileListActions {...sharedFileUploadProps} actions={sharedActions} />
                                <div className={styles['file-list']}>
                                    {sharedPromptItems.length > 0 ? (
                                        <ListItems
                                            items={sharedPromptItems}
                                            itemsSelected={sharedSelectedIds}
                                            loadingItemIds={sharedLoadingIds}
                                            childrenClassName={styles.children}
                                            headerClassName={styles.header}
                                        />
                                    ) : (
                                        <p>{labels.noResultsFound}</p>
                                    )}
                                </div>
                            </div>

                        }
                    />}
                />
            </div>
        </>
    );
};

export default PromptList;
